import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  Users, Check, X, Loader2, Shield, Headphones, MessageSquare,
  ChevronDown, ChevronRight, Phone, UserCog, Plus, Trash2, UserPlus,
  GraduationCap, BookOpen, Key, Copy, Eye, EyeOff, Package, UserCheck, Building2,
  FileText, PenTool, Sparkles, ClipboardList, Trophy, Share2, Star, IndianRupee
} from 'lucide-react';

const ModuleAccess = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('access'); // 'access' or 'credentials'
  const [teacherCredentials, setTeacherCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({
    manager: true,
    caller: true,
    complaint_entry: true,
    complaint_solver: true,
    class_conduction_agent: true,
    teacher: true,
    stock_manager: true,
    typing_manager: true,
    typing_agent: true,
    offline_manager: true,
    counsellor: true,
    result_agent: true,
    marketing_agent: true,
    doubt_engine_manager: true,
    feedback_manager: true
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalPresetRole, setAddModalPresetRole] = useState(null); // Track if role was preset
  const [showAddManagerModal, setShowAddManagerModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'caller'
  });
  const [newManager, setNewManager] = useState({
    name: '',
    email: '',
    password: '',
    module_access: []
  });
  const [addingUser, setAddingUser] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [userToPromote, setUserToPromote] = useState(null);
  const [promoteModules, setPromoteModules] = useState([]);
  const [promoting, setPromoting] = useState(false);

  // Available modules - single source of truth for all module-related features
  const modules = [
    { id: 'sales_calling', name: 'Sales Calling', icon: Headphones, description: 'Lead management, caller groups, and sales dashboard' },
    { id: 'complaint_entry', name: 'Complaint Entry', icon: MessageSquare, description: 'Register new complaints from students/calls' },
    { id: 'complaint_solving', name: 'Complaint Solving', icon: MessageSquare, description: 'Assign, work, and resolve complaint tickets' },
    { id: 'class_delivery', name: 'Class Delivery', icon: GraduationCap, description: 'Class scheduling and teacher management' },
    { id: 'typing', name: 'Typing', icon: FileText, description: 'PPT creation and management for teachers' },
    { id: 'content', name: 'Content', icon: BookOpen, description: 'Questions repository and book library' },
    { id: 'books_stock', name: 'Books Stock', icon: Package, description: 'Inventory management, stock transfers, and reporting' },
    { id: 'hr', name: 'HR', icon: UserCheck, description: 'Employee management, documents, and salary' },
    { id: 'finance', name: 'Finance', icon: Building2, description: 'Finance dashboard and Tally integration' },
    { id: 'offline_centre', name: 'Offline Centre', icon: Building2, description: 'Student enquiries, admissions, batches, and offline classes' },
    { id: 'special_events', name: 'Special Events', icon: Sparkles, description: 'YouTube special events for students - creation, review, and tracking' },
    { id: 'task_manager', name: 'Task Manager', icon: ClipboardList, description: 'Daily task assignment and morning meeting tracker' },
    { id: 'results', name: 'Results', icon: Trophy, description: 'Track exam qualifications and verified results' },
    { id: 'ecommerce', name: 'E-Commerce', icon: Package, description: 'Exampur Store orders, products, and revenue tracking' },
    { id: 'marketing', name: 'Marketing', icon: Sparkles, description: 'Campaign design, daily check-ins & results tracking' },
    { id: 'doubt_engine', name: 'Doubt Engine', icon: MessageSquare, description: 'Batch-wise student ↔ teacher chat (with images & voice notes)' },
    { id: 'teacher_feedback', name: 'Teacher Feedback', icon: Star, description: 'Per-batch feedback forms — students rate teachers 1-5 stars' },
    { id: 'complaint_chat', name: 'Complaint Live Chat', icon: MessageSquare, description: 'Real-time chat with complainants — 30s auto-fallback to ticket' },
    { id: 'cash_flow', name: 'Cash Flow', icon: IndianRupee, description: 'Plan daily expenses on a calendar with per-manager daily limits' },
  ];

  // Module definitions for module access management (use same as modules for consistency)
  const moduleDefinitions = modules;

  // Role definitions with default modules
  const roleDefinitions = {
    manager: { 
      name: 'Managers', 
      singularName: 'Manager',
      defaultModules: ['sales_calling'],
      icon: UserCog,
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700'
    },
    caller: { 
      name: 'Sales Calling Agents', 
      singularName: 'Sales Calling Agent',
      defaultModules: ['sales_calling'],
      icon: Phone,
      color: 'green',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700'
    },
    complaint_entry: { 
      name: 'Complaint Entry Agents', 
      singularName: 'Complaint Entry Agent',
      defaultModules: ['complaint_entry'],
      icon: MessageSquare,
      color: 'orange',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700'
    },
    complaint_solver: { 
      name: 'Complaint Solving Agents', 
      singularName: 'Complaint Solving Agent',
      defaultModules: ['complaint_solving'],
      icon: MessageSquare,
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    },
    class_conduction_agent: { 
      name: 'Class Conduction Agents', 
      singularName: 'Class Conduction Agent',
      defaultModules: ['class_delivery'],
      icon: BookOpen,
      color: 'purple',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700'
    },
    teacher: { 
      name: 'Teachers', 
      singularName: 'Teacher',
      defaultModules: ['class_delivery'],
      icon: GraduationCap,
      color: 'indigo',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-700'
    },
    stock_manager: { 
      name: 'Stock Managers', 
      singularName: 'Stock Manager',
      defaultModules: ['books_stock'],
      icon: Package,
      color: 'cyan',
      bgColor: 'bg-cyan-100',
      textColor: 'text-cyan-700'
    },
    typing_manager: { 
      name: 'Typing Managers', 
      singularName: 'Typing Manager',
      defaultModules: ['typing'],
      icon: FileText,
      color: 'rose',
      bgColor: 'bg-rose-100',
      textColor: 'text-rose-700'
    },
    typing_agent: { 
      name: 'Typing Agents', 
      singularName: 'Typing Agent',
      defaultModules: ['typing'],
      icon: PenTool,
      color: 'pink',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-700'
    },
    content_manager: { 
      name: 'Content Managers', 
      singularName: 'Content Manager',
      defaultModules: ['content'],
      icon: BookOpen,
      color: 'emerald',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-700'
    },
    content_agent: { 
      name: 'Content Agents', 
      singularName: 'Content Agent',
      defaultModules: ['content'],
      icon: FileText,
      color: 'teal',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-700'
    },
    offline_manager: { 
      name: 'Offline Managers', 
      singularName: 'Offline Manager',
      defaultModules: ['offline_centre'],
      icon: Building2,
      color: 'sky',
      bgColor: 'bg-sky-100',
      textColor: 'text-sky-700'
    },
    counsellor: { 
      name: 'Counsellors', 
      singularName: 'Counsellor',
      defaultModules: ['offline_centre'],
      icon: Users,
      color: 'violet',
      bgColor: 'bg-violet-100',
      textColor: 'text-violet-700'
    },
    special_events_agent: { 
      name: 'Special Event Conduction Agents', 
      singularName: 'Special Event Conduction Agent',
      defaultModules: ['special_events'],
      icon: Sparkles,
      color: 'rose',
      bgColor: 'bg-rose-100',
      textColor: 'text-rose-700'
    },
    result_agent: { 
      name: 'Result Agents', 
      singularName: 'Result Agent',
      defaultModules: ['results'],
      icon: Trophy,
      color: 'emerald',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-700'
    },
    marketing_agent: {
      name: 'Marketing Agents',
      singularName: 'Marketing Agent',
      defaultModules: ['marketing'],
      icon: Sparkles,
      color: 'pink',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-700'
    },
    doubt_engine_manager: {
      name: 'Doubt Engine Managers',
      singularName: 'Doubt Engine Manager',
      defaultModules: ['doubt_engine'],
      icon: MessageSquare,
      color: 'emerald',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-700'
    },
    feedback_manager: {
      name: 'Feedback Managers',
      singularName: 'Feedback Manager',
      defaultModules: ['teacher_feedback'],
      icon: Star,
      color: 'amber',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'credentials') {
      fetchTeacherCredentials();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await api.getUsers();
      // Filter out admins (they have full access) and add module_access field
      const usersWithAccess = response.data
        .filter(u => u.role !== 'admin' && u.is_approved !== false)
        .map(u => ({
          ...u,
          module_access: u.module_access || roleDefinitions[u.role]?.defaultModules || []
        }));
      setUsers(usersWithAccess);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const response = await api.getTeacherCredentials();
      setTeacherCredentials(response.data);
    } catch (error) {
      toast.error('Failed to fetch teacher credentials');
    } finally {
      setCredentialsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAllCredentials = () => {
    const text = teacherCredentials.map(t => 
      `${t.name}\nEmail: ${t.email}\nPassword: (initial password based on employee ID)`
    ).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('All credentials copied to clipboard');
  };

  const toggleModuleAccess = async (userId, moduleId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const currentAccess = user.module_access || [];
    const hasAccess = currentAccess.includes(moduleId);
    const newAccess = hasAccess 
      ? currentAccess.filter(id => id !== moduleId)
      : [...currentAccess, moduleId];

    // Update UI optimistically
    setUsers(prev => prev.map(u => 
      u.id === userId ? { ...u, module_access: newAccess } : u
    ));

    setSaving(prev => ({ ...prev, [userId]: true }));

    try {
      await api.updateModuleAccess(userId, newAccess);
      toast.success(`Module access updated for ${user.name}`);
    } catch (error) {
      // Revert on error
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, module_access: currentAccess } : u
      ));
      toast.error(error.response?.data?.detail || 'Failed to update module access');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [userId]: true }));

    try {
      await api.deleteUser(userId);
      toast.success(`${userName} has been deleted`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    setAddingUser(true);

    try {
      const response = await api.createUser(newUser);
      toast.success(`${newUser.name} has been added as ${roleDefinitions[newUser.role]?.singularName || newUser.role}`);
      
      // Add to list with default module access
      const newUserData = {
        ...response.data,
        module_access: roleDefinitions[newUser.role]?.defaultModules || []
      };
      setUsers(prev => [...prev, newUserData]);
      
      // Reset form and close modal
      setNewUser({ name: '', email: '', password: '', role: 'caller' });
      setAddModalPresetRole(null);
      setShowAddModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    
    if (!newManager.name.trim() || !newManager.email.trim() || !newManager.password.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    if (newManager.module_access.length === 0) {
      toast.error('Please select at least one module');
      return;
    }

    setAddingManager(true);

    try {
      const response = await api.createManager({
        name: newManager.name,
        email: newManager.email,
        password: newManager.password,
        module_access: newManager.module_access
      });
      toast.success(`${newManager.name} has been added as Manager`);
      
      // Add to list
      const newManagerData = {
        ...response.data,
        module_access: response.data.module_access
      };
      setUsers(prev => [...prev, newManagerData]);
      
      // Reset form and close modal
      setNewManager({ name: '', email: '', password: '', module_access: [] });
      setShowAddManagerModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add manager');
    } finally {
      setAddingManager(false);
    }
  };

  const toggleManagerModule = (moduleId) => {
    setNewManager(prev => ({
      ...prev,
      module_access: prev.module_access.includes(moduleId)
        ? prev.module_access.filter(m => m !== moduleId)
        : [...prev.module_access, moduleId]
    }));
  };

  const togglePromoteModule = (moduleId) => {
    setPromoteModules(prev => 
      prev.includes(moduleId)
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handlePromoteToManager = async (e) => {
    e.preventDefault();
    
    if (!userToPromote) return;
    
    if (promoteModules.length === 0) {
      toast.error('Please select at least one module for the manager');
      return;
    }

    setPromoting(true);

    try {
      await api.promoteToManager(userToPromote.id, promoteModules);
      toast.success(`${userToPromote.name} has been promoted to Manager`);
      
      // Update user in list
      setUsers(prev => prev.map(u => 
        u.id === userToPromote.id 
          ? { ...u, role: 'manager', module_access: promoteModules }
          : u
      ));
      
      // Reset and close modal
      setShowPromoteModal(false);
      setUserToPromote(null);
      setPromoteModules([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromoting(false);
    }
  };

  const openPromoteModal = (user) => {
    setUserToPromote(user);
    setPromoteModules(user.module_access || []);
    setShowPromoteModal(true);
  };

  const toggleRoleExpand = (role) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const getUsersByRole = (role) => {
    return users.filter(u => u.role === role);
  };

  const getRoleBadgeColor = (role) => {
    return `${roleDefinitions[role]?.bgColor || 'bg-slate-100'} ${roleDefinitions[role]?.textColor || 'text-slate-600'}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Agent roles only (exclude all manager-type roles from agent sections)
  const managerTypeRoles = ['manager', 'stock_manager', 'typing_manager', 'content_manager'];
  const agentRoles = Object.keys(roleDefinitions).filter(r => !managerTypeRoles.includes(r));
  
  const filteredRoles = roleFilter === 'all' 
    ? agentRoles 
    : roleFilter === 'manager' 
      ? [] // Managers are shown separately
      : managerTypeRoles.includes(roleFilter)
        ? [] // Manager-type roles shown in Managers section
        : [roleFilter];

  // Get all managers (including stock_manager and typing_manager)
  const managers = users.filter(u => managerTypeRoles.includes(u.role));

  return (
    <div data-testid="module-access-page" className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2 flex items-center gap-3">
            <Shield className="text-slate-700" />
            Module Access Control
          </h1>
          <p className="text-slate-600 font-body">
            Add, remove, and control module access for all agents. Users only see tabs for modules they have access to.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAddManagerModal(true)}
            data-testid="add-manager-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <UserCog size={20} />
            Add Manager
          </button>
          <button
            onClick={() => {
              setAddModalPresetRole(null);
              setNewUser(prev => ({ ...prev, role: 'caller' }));
              setShowAddModal(true);
            }}
            data-testid="add-agent-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <UserPlus size={20} />
            Add Agent
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('access')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'access'
              ? 'text-slate-900 border-b-2 border-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            Module Access
          </div>
        </button>
        <button
          onClick={() => setActiveTab('credentials')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'credentials'
              ? 'text-slate-900 border-b-2 border-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Key size={18} />
            Teacher Credentials
          </div>
        </button>
      </div>

      {activeTab === 'credentials' ? (
        /* Teacher Credentials Tab */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <GraduationCap size={24} className="text-indigo-600" />
                Teacher Login Credentials
              </h2>
              <p className="text-slate-500 text-sm mt-1">Share these credentials with teachers for login access</p>
            </div>
            <button
              onClick={copyAllCredentials}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              <Copy size={18} />
              Copy All
            </button>
          </div>
          
          {credentialsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : teacherCredentials.length === 0 ? (
            <div className="p-12 text-center">
              <GraduationCap size={48} className="mx-auto text-slate-200 mb-4" />
              <p className="text-slate-500">No teachers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email (Username)</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {teacherCredentials.map((teacher, index) => (
                    <tr key={teacher.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-500">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{teacher.name}</div>
                        {teacher.phone && (
                          <div className="text-xs text-slate-500">{teacher.phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm">{teacher.email}</code>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          teacher.is_approved 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {teacher.is_approved ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => copyToClipboard(teacher.email)}
                          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                          title="Copy email"
                        >
                          <Copy size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="p-4 bg-amber-50 border-t border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Default password for teachers is based on their Employee ID (e.g., "exam5215" for EXAM-5215). 
              For new teachers without an ID, the default password is "exampur123". 
              Teachers can request password reset via the login page.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Available Modules Info */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4">Available Modules</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modules.map((module) => {
                const Icon = module.icon;
                return (
                  <div key={module.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Icon size={20} className="text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 font-body">{module.name}</h3>
                    </div>
                    <p className="text-sm text-slate-500 font-body">{module.description}</p>
                  </div>
            );
          })}
          <div className="p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300 flex items-center justify-center">
            <p className="text-sm text-slate-400 font-body text-center">
              More modules coming soon...<br />
              (Inventory, etc.)
            </p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-slate-700 font-body">Filter by Role:</label>
          <select
            data-testid="role-filter-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
          >
            <option value="all">All Roles</option>
            <option value="manager">Managers</option>
            <option value="caller">Sales Calling Agents</option>
            <option value="complaint_entry">Complaint Entry Agents</option>
            <option value="complaint_solver">Complaint Solving Agents</option>
            <option value="class_conduction_agent">Class Conduction Agents</option>
            <option value="teacher">Teachers</option>
            <option value="typing_manager">Typing Managers</option>
            <option value="typing_agent">Typing Agents</option>
            <option value="stock_manager">Stock Managers</option>
            <option value="special_events_agent">Special Event Agents</option>
            <option value="result_agent">Result Agents</option>
            <option value="marketing_agent">Marketing Agents</option>
            <option value="doubt_engine_manager">Doubt Engine Managers</option>
            <option value="feedback_manager">Feedback Managers</option>
          </select>
          <span className="text-sm text-slate-500 font-body ml-auto">
            {users.length} agents total (excluding admins)
          </span>
        </div>
      </div>

      {/* Managers Section - Separate from Agents */}
      {(roleFilter === 'all' || roleFilter === 'manager') && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-blue-200 bg-blue-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                <UserCog size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold font-headings text-slate-900">
                  Managers
                </h2>
                <p className="text-sm text-slate-500 font-body">
                  {managers.length} manager{managers.length !== 1 ? 's' : ''} - Can access multiple modules
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAddManagerModal(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-body flex items-center gap-1"
            >
              <Plus size={14} />
              Add Manager
            </button>
          </div>

          {managers.length === 0 ? (
            <div className="p-8 text-center">
              <UserCog size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-body">No managers yet</p>
              <p className="text-sm text-slate-400 font-body mt-1">
                Promote an agent to manager or add a new manager
              </p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[65vh] border border-slate-200 rounded-lg">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="sticky top-0 left-0 z-30 bg-slate-50 px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-r border-slate-200">
                      Name
                    </th>
                    <th className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                      Email
                    </th>
                    {modules.map((module) => (
                      <th key={module.id} className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        {module.name}
                      </th>
                    ))}
                    <th className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {managers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-6 py-4 border-r border-slate-200">
                        <div className="font-medium text-slate-900 font-body">{user.name}</div>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Manager</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                        {user.email}
                      </td>
                      {modules.map((module) => {
                        const hasAccess = (user.module_access || []).includes(module.id);
                        const isSaving = saving[user.id];
                        return (
                          <td key={module.id} className="px-6 py-4 text-center">
                            <button
                              data-testid={`toggle-manager-${user.id}-${module.id}`}
                              onClick={() => toggleModuleAccess(user.id, module.id)}
                              disabled={isSaving}
                              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all mx-auto ${
                                hasAccess 
                                  ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' 
                                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {isSaving ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : hasAccess ? (
                                <Check size={18} />
                              ) : (
                                <X size={18} />
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 text-center">
                        <button
                          data-testid={`delete-manager-${user.id}`}
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          disabled={deleting[user.id]}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete manager"
                        >
                          {deleting[user.id] ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Agent Roles Section */}
      {filteredRoles.length > 0 && (
        <div className="pt-2">
          <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Users size={20} />
            Agents by Role
          </h2>
        </div>
      )}

      {/* User Access by Role */}
      <div className="space-y-4">
        {filteredRoles.map((role) => {
          const roleUsers = getUsersByRole(role);
          const roleDef = roleDefinitions[role];
          const Icon = roleDef?.icon || Users;
          const isExpanded = expandedRoles[role];

          return (
            <div key={role} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Role Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <button
                  onClick={() => toggleRoleExpand(role)}
                  className="flex items-center gap-3 flex-1"
                >
                  <div className={`p-2 rounded-lg ${getRoleBadgeColor(role)}`}>
                    <Icon size={20} />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-semibold font-headings text-slate-900">
                      {roleDef?.name || role}
                    </h2>
                    <p className="text-sm text-slate-500 font-body">
                      {roleUsers.length} agent{roleUsers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewUser(prev => ({ ...prev, role }));
                      setAddModalPresetRole(role);
                      setShowAddModal(true);
                    }}
                    className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-body flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                  <button onClick={() => toggleRoleExpand(role)} className="p-1">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </button>
                </div>
              </div>

              {/* Users Table */}
              {isExpanded && (
                <>
                  {roleUsers.length === 0 ? (
                    <div className="p-8 text-center">
                      <Users size={40} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500 font-body">No {roleDef?.name?.toLowerCase() || 'agents'} yet</p>
                      <button
                        onClick={() => {
                          setNewUser(prev => ({ ...prev, role }));
                          setAddModalPresetRole(role);
                          setShowAddModal(true);
                        }}
                        className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-body"
                      >
                        + Add {roleDef?.singularName || 'agent'}
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-[65vh] border border-slate-200 rounded-lg">
                      <table className="w-full border-collapse">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="sticky top-0 left-0 z-30 bg-slate-50 px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-r border-slate-200">
                              Name
                            </th>
                            <th className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                              Email
                            </th>
                            {modules.map((module) => (
                              <th key={module.id} className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                                {module.name}
                              </th>
                            ))}
                            <th className="sticky top-0 z-20 bg-slate-50 px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {roleUsers.map((user) => (
                            <tr key={user.id} className="hover:bg-slate-50 transition-colors group">
                              <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-6 py-4 border-r border-slate-200">
                                <div className="font-medium text-slate-900 font-body">{user.name}</div>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                                {user.email}
                              </td>
                              {modules.map((module) => {
                                const hasAccess = (user.module_access || []).includes(module.id);
                                const isSaving = saving[user.id];
                                return (
                                  <td key={module.id} className="px-6 py-4 text-center">
                                    <button
                                      data-testid={`toggle-${user.id}-${module.id}`}
                                      onClick={() => toggleModuleAccess(user.id, module.id)}
                                      disabled={isSaving}
                                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all mx-auto ${
                                        hasAccess 
                                          ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' 
                                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      {isSaving ? (
                                        <Loader2 size={18} className="animate-spin" />
                                      ) : hasAccess ? (
                                        <Check size={18} />
                                      ) : (
                                        <X size={18} />
                                      )}
                                    </button>
                                  </td>
                                );
                              })}
                              <td className="px-6 py-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    data-testid={`promote-${user.id}`}
                                    onClick={() => openPromoteModal(user)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Promote to Manager"
                                  >
                                    <UserCog size={18} />
                                  </button>
                                  <button
                                    data-testid={`delete-${user.id}`}
                                    onClick={() => handleDeleteUser(user.id, user.name)}
                                    disabled={deleting[user.id]}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Delete agent"
                                  >
                                    {deleting[user.id] ? (
                                      <Loader2 size={18} className="animate-spin" />
                                    ) : (
                                      <Trash2 size={18} />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {users.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-body">No agents found. Click "Add Agent" to create one.</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-700 font-body">
          <strong>Note:</strong> Changes take effect immediately. Users need to refresh their browser 
          to see updated navigation. Admins always have full access and are not shown here.
        </p>
      </div>
        </>
      )}

      {/* Add Agent Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold font-headings text-slate-900">
                  {addModalPresetRole 
                    ? `Add New ${roleDefinitions[addModalPresetRole]?.singularName || 'Agent'}`
                    : 'Add New Agent'}
                </h2>
                <p className="text-sm text-slate-500 font-body mt-1">Create a new user account</p>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddModalPresetRole(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              {/* Only show role selector if no preset role */}
              {!addModalPresetRole && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                    Agent Type *
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                    data-testid="new-agent-role"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
                  >
                    <optgroup label="Sales Calling Module">
                      <option value="caller">Sales Calling Agent</option>
                    </optgroup>
                    <optgroup label="Complaints Module">
                      <option value="complaint_entry">Complaint Entry Agent</option>
                      <option value="complaint_solver">Complaint Solving Agent</option>
                    </optgroup>
                    <optgroup label="Class Delivery Module">
                      <option value="class_conduction_agent">Class Conduction Agent</option>
                      <option value="teacher">Teacher</option>
                    </optgroup>
                    <optgroup label="Typing Module">
                      <option value="typing_manager">Typing Manager</option>
                      <option value="typing_agent">Typing Agent</option>
                    </optgroup>
                    <optgroup label="Books Stock Module">
                      <option value="stock_manager">Stock Manager</option>
                    </optgroup>
                    <optgroup label="Special Events Module">
                      <option value="special_events_agent">Special Event Conduction Agent</option>
                    </optgroup>
                    <optgroup label="Results Module">
                      <option value="result_agent">Result Agent</option>
                    </optgroup>
                    <optgroup label="Marketing Module">
                      <option value="marketing_agent">Marketing Agent</option>
                    </optgroup>
                    <optgroup label="Doubt Engine Module">
                      <option value="doubt_engine_manager">Doubt Engine Manager</option>
                    </optgroup>
                    <optgroup label="Teacher Feedback Module">
                      <option value="feedback_manager">Feedback Manager</option>
                    </optgroup>
                  </select>
                  <p className="text-xs text-slate-500 mt-1 font-body">
                    {newUser.role === 'caller' && 'Will have access to Sales Calling module by default'}
                    {newUser.role === 'complaint_entry' && 'Will have access to Complaints module by default'}
                    {newUser.role === 'complaint_solver' && 'Will have access to Complaints module by default'}
                    {newUser.role === 'class_conduction_agent' && 'Will have access to Class Delivery module by default'}
                    {newUser.role === 'teacher' && 'Will have access to Class Delivery module by default'}
                    {newUser.role === 'typing_manager' && 'Will have access to Typing module by default'}
                    {newUser.role === 'typing_agent' && 'Will have access to Typing module by default'}
                    {newUser.role === 'stock_manager' && 'Will have access to Books Stock module by default'}
                    {newUser.role === 'special_events_agent' && 'Will have access to Special Events module by default'}
                    {newUser.role === 'result_agent' && 'Will have access to Results module by default'}
                    {newUser.role === 'marketing_agent' && 'Will have access to Marketing module by default'}
                    {newUser.role === 'doubt_engine_manager' && 'Will have access to Doubt Engine module by default'}
                    {newUser.role === 'feedback_manager' && 'Will have access to Teacher Feedback module by default'}
                  </p>
                </div>
              )}

              {/* Show selected role info when preset */}
              {addModalPresetRole && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm text-slate-600 font-body">
                    <span className="font-medium">Role:</span> {roleDefinitions[addModalPresetRole]?.singularName || addModalPresetRole}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Will have access to {roleDefinitions[addModalPresetRole]?.defaultModules?.join(', ') || 'assigned'} module(s) by default
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="new-agent-name"
                  placeholder="Full name"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="new-agent-email"
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  data-testid="new-agent-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddModalPresetRole(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 font-body"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingUser}
                  data-testid="submit-new-agent"
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                >
                  {addingUser ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Add Agent
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Manager Modal */}
      {showAddManagerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900 font-headings flex items-center gap-2">
                <UserCog size={24} className="text-blue-600" />
                Add New Manager
              </h2>
              <button
                onClick={() => setShowAddManagerModal(false)}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddManager} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newManager.name}
                  onChange={(e) => setNewManager(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="new-manager-name"
                  placeholder="Full name"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newManager.email}
                  onChange={(e) => setNewManager(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="new-manager-email"
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={newManager.password}
                  onChange={(e) => setNewManager(prev => ({ ...prev, password: e.target.value }))}
                  data-testid="new-manager-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">
                  Module Access * <span className="text-slate-400 font-normal">(Select modules this manager will head)</span>
                </label>
                <div className="space-y-2">
                  {modules.map((module) => {
                    const Icon = module.icon;
                    const isSelected = newManager.module_access.includes(module.id);
                    return (
                      <label
                        key={module.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-slate-50 border-slate-200 hover:border-blue-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleManagerModule(module.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <Icon size={18} className={isSelected ? 'text-blue-600' : 'text-slate-400'} />
                        <div>
                          <p className="font-medium text-slate-900">{module.name}</p>
                          <p className="text-xs text-slate-500">{module.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddManagerModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 font-body"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingManager}
                  data-testid="submit-new-manager"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                >
                  {addingManager ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Add Manager
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promote to Manager Modal */}
      {showPromoteModal && userToPromote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold font-headings text-slate-900">
                  Promote to Manager
                </h2>
                <p className="text-sm text-slate-500 font-body mt-1">
                  Promote {userToPromote.name} to manager role
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPromoteModal(false);
                  setUserToPromote(null);
                  setPromoteModules([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePromoteToManager} className="p-6 space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <UserCog size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{userToPromote.name}</p>
                    <p className="text-sm text-slate-500">{userToPromote.email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Current role: {roleDefinitions[userToPromote.role]?.singularName || userToPromote.role}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-3">
                  Select Modules for Manager Access *
                </label>
                <div className="space-y-2">
                  {modules.map((module) => {
                    const Icon = module.icon;
                    const isSelected = promoteModules.includes(module.id);
                    return (
                      <button
                        key={module.id}
                        type="button"
                        onClick={() => togglePromoteModule(module.id)}
                        className={`w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-slate-100'}`}>
                          <Icon size={18} className={isSelected ? 'text-blue-600' : 'text-slate-500'} />
                        </div>
                        <div className="flex-1">
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>
                            {module.name}
                          </span>
                          <p className="text-xs text-slate-500">{module.description}</p>
                        </div>
                        {isSelected && (
                          <Check size={18} className="text-blue-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  The promoted manager will be able to manage agents in the selected modules
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPromoteModal(false);
                    setUserToPromote(null);
                    setPromoteModules([]);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 font-body"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={promoting || promoteModules.length === 0}
                  data-testid="submit-promote"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-body"
                >
                  {promoting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Promoting...
                    </>
                  ) : (
                    <>
                      <UserCog size={18} />
                      Promote to Manager
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleAccess;
